import React, { useEffect, useMemo, useRef, useState } from "react";
import { Modal, OverlayTrigger, Tooltip } from "react-bootstrap";
import moment from "moment";
import { toast } from "react-toastify";
import { useNavigate } from "react-router-dom";
import downIcon from "../../../asset/images/download-blue-arrow.svg";
import filter from "../../../asset/images/preference-horizontal.svg";
import infoIcon from "../../../asset/images/info-circle.svg";
import axiosInstance from "../../../CommonMethhods/axiosInstance";
import { Dealer_Mapping } from "../../../Configuration/API_URL";
import { decryptData, formatDate, noDataFound } from "../../../CommonMethhods/CommonMethods";
import CustomTradeDate from "../../../CommonMethhods/CustomDate";
import Loader from "../../../Components/Common/Loader";
import SmallLoader from "../../../Components/Common/smallLoader";
import CheckerPagination from "./CheckerPagination";
import { getReasonTypeLabel } from "./checkerUtils";

const isBulkBatch = (batch) => String(batch?.batchId || "").toUpperCase().startsWith("BLK");

function PendingApprovals() {
  const navigate = useNavigate();

  const [checkerData, setCheckerData] = useState([]);
  const [loader, setLoader] = useState(false);
  const [downloadLoader, setDownloadLoader] = useState(false);
  const [search, setSearch] = useState("");
  const [dealerFilter, setDealerFilter] = useState("all");
  const [typeFilter, setTypeFilter] = useState("all");
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");
  // Filters currently applied by the modal - the inputs above only take effect on Apply.
  const [applied, setApplied] = useState({ type: "all", from: "", to: "" });
  const [filterModal, setFilterModal] = useState(false);
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(5);
  const filterRef = useRef(null);

  const chkReviewTableData = async () => {
    setLoader(true);

    try {
      // pending-batches has no batch-level reason / remarks (what the maker
      // typed for the whole request), so they're joined in from the checker's
      // request list. Best-effort - the queue still renders without them.
      const [getDetails, summaries] = await Promise.all([
        axiosInstance.get(Dealer_Mapping.checkerPreview),
        axiosInstance
          .get(
            `${Dealer_Mapping.checkerMyRequest}?${new URLSearchParams({
              format: "grid",
              status: "all",
              type: "all",
              fromDate: "",
              toDate: "",
              batchIdRef: "",
            })}`
          )
          .then((response) => JSON.parse(decryptData(response.data)))
          .catch(() => null),
      ]);
      const res = JSON.parse(decryptData(getDetails.data));
      if (res?.status === 0) {
        const summaryById = {};
        (summaries?.data || []).forEach((item) => {
          summaryById[String(item?.batchId).toUpperCase()] = item;
        });

        setCheckerData(
          (res?.data || []).map((batch) => {
            const summary = summaryById[String(batch?.batchId).toUpperCase()];
            return {
              ...batch,
              reason: summary?.reason,
              remarks: summary?.remarks,
            };
          })
        );
      } else {
        setCheckerData([]);
        toast.error(res?.message);
      }
    } catch (error) {
      console.log(error);
      setCheckerData([]);
    } finally {
      setLoader(false);
    }
  };

  useEffect(() => {
    chkReviewTableData();
  }, []);

  useEffect(() => {
    setPage(1);
  }, [search, dealerFilter, applied]);

  const dealerOptions = useMemo(
    () => [
      ...new Set(
        checkerData
          .flatMap((batch) => (batch?.requests || []).map((row) => row?.newDealerCode))
          .filter(Boolean)
      ),
    ],
    [checkerData]
  );

  const filteredData = useMemo(() => {
    const searchText = search.trim().toLowerCase();

    return checkerData.filter((batch) => {
      const requests = batch?.requests || [];

      if (applied.type === "single" && isBulkBatch(batch)) return false;
      if (applied.type === "bulk" && !isBulkBatch(batch)) return false;

      if (dealerFilter !== "all" && !requests.some((row) => row?.newDealerCode === dealerFilter)) {
        return false;
      }

      if (applied.from || applied.to) {
        const submitted = moment(batch?.submittedAt);
        if (applied.from && submitted.isBefore(moment(applied.from, "DD-MM-YYYY"), "day")) {
          return false;
        }
        if (applied.to && submitted.isAfter(moment(applied.to, "DD-MM-YYYY"), "day")) {
          return false;
        }
      }

      if (!searchText) return true;

      return [
        batch?.batchId,
        batch?.makerId,
        batch?.makerName,
        ...requests.flatMap((row) => [row?.ucc, row?.clientName, row?.newDealerCode]),
      ]
        .filter(Boolean)
        .some((value) => String(value).toLowerCase().includes(searchText));
    });
  }, [checkerData, search, dealerFilter, applied]);

  const totalCount = filteredData.length;
  const paginatedData = filteredData.slice((page - 1) * limit, page * limit);

  const handleViewDetails = (batch) => {
    if (!batch?.batchId) return;

    navigate(`/dealer-mapping/checker-request/${batch.batchId}`, {
      state: { request: { ...batch, status: batch?.status || "pending" } },
    });
  };

  const handleApplyFilter = () => {
    if (!fromDate && !toDate) {
      setApplied({ type: typeFilter, from: "", to: "" });
      setFilterModal(false);
      return;
    }

    if (!fromDate || !toDate) {
      toast.error("Please select both From Date and To Date");
      return;
    }

    const from = moment(fromDate, "DD-MM-YYYY", true);
    const to = moment(toDate, "DD-MM-YYYY", true);
    const today = moment().startOf("day");

    if (!from.isValid() || !to.isValid()) {
      toast.error("Please enter valid dates");
      return;
    }

    if (from.isAfter(today)) {
      toast.error("From Date cannot be a future date");
      return;
    }

    if (to.isAfter(today)) {
      toast.error("To Date cannot be a future date");
      return;
    }

    if (to.isBefore(from)) {
      toast.error("To Date cannot be earlier than From Date");
      return;
    }

    if (to.diff(from, "days") > 31) {
      toast.error("Date range should not exceed 1 month");
      return;
    }

    setApplied({ type: typeFilter, from: fromDate, to: toDate });
    setFilterModal(false);
  };

  const handleResetFilter = () => {
    setTypeFilter("all");
    setFromDate("");
    setToDate("");
    setApplied({ type: "all", from: "", to: "" });
    setFilterModal(false);
  };

  const handleCloseFilter = () => {
    // Discard un-applied edits.
    setTypeFilter(applied.type);
    setFromDate(applied.from);
    setToDate(applied.to);
    setFilterModal(false);
  };

  // The pending queue has no export of its own, so the checker's request
  // history export is used (the backend can't filter it to pending only).
  const handleDownload = async () => {
    setDownloadLoader(true);
    try {
      const queryParams = new URLSearchParams({
        format: "file",
        status: "all",
        type: applied.type,
        fromDate: applied.from ? moment(applied.from, "DD-MM-YYYY").format("YYYY-MM-DD") : "",
        toDate: applied.to ? moment(applied.to, "DD-MM-YYYY").format("YYYY-MM-DD") : "",
        batchIdRef: "",
      });
      const response = await axiosInstance.get(`${Dealer_Mapping.checkerMyRequest}?${queryParams}`);
      const decrypted = JSON.parse(decryptData(response.data));

      if (decrypted?.status === 0) {
        toast.success(decrypted?.message);
        window.open(decrypted?.data?.url, "_self", "noopener,noreferrer");
      } else {
        toast.error(decrypted?.message);
      }
    } catch (error) {
      toast.error("Something went wrong");
    } finally {
      setDownloadLoader(false);
    }
  };

  const currentDate = moment().format("YYYY-MM-DD");
  const isDownloadDisabled = checkerData.length === 0;

  // const renderRemarks = (text) => {
  //   if (!text) return "-";
  //   return (
  //     <OverlayTrigger placement="top" overlay={<Tooltip id="remarks-tooltip" className="dm-reason-tooltip">{text}</Tooltip>}>
  //       <span className="dm-reason-text">{text}</span>
  //     </OverlayTrigger>
  //   );
  // };
  const renderRemarks = (text) => <span className="dm-reason-text">{text || "-"}</span>;

  return (
    <>
      <div className="off-market-main">
        <div className="ckr-page new-upload">
          <div className="ckr-section history-table">
            <div className="ckr-section-header">
              <p className="ckr-section-title">Pending Requests</p>

              <div className="d-flex align-items-center header-filter">
                <div className="filter-options offmark-filter">
                  <div className="dropdown-filter focusable-filter">
                    <select
                      aria-label="Filter by dealer"
                      value={dealerFilter}
                      onChange={(e) => setDealerFilter(e.target.value)}
                    >
                      <option value="all">All Dealers</option>
                      {dealerOptions.map((code) => (
                        <option key={code} value={code}>
                          {code}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>

                <div
                  className="filter-wrapper focusable-filter"
                  tabIndex={0}
                  ref={filterRef}
                  role="button"
                  onClick={() => setFilterModal(true)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" || e.key === " ") {
                      e.preventDefault();
                      setFilterModal(true);
                    }
                  }}
                >
                  <img src={filter} alt="filter" />
                </div>

                <div className="ckr-search-box focusable-filter">
                  <i className="fa fa-search" />
                  <input
                    type="text"
                    placeholder="Search by client code/name"
                    value={search}
                    maxLength={30}
                    onChange={(e) => setSearch(e.target.value)}
                  />
                </div>

                <div
                  className={`wrap-down-btn focusable-filter ${
                    isDownloadDisabled ? "disabled-btn-download" : ""
                  }`}
                  role="button"
                  tabIndex={isDownloadDisabled ? -1 : 0}
                  onClick={() => {
                    if (isDownloadDisabled || downloadLoader) return;
                    handleDownload();
                  }}
                  onKeyDown={(e) => {
                    if ((e.key === "Enter" || e.key === " ") && !isDownloadDisabled && !downloadLoader) {
                      e.preventDefault();
                      handleDownload();
                    }
                  }}
                >
                  {downloadLoader ? (
                    <SmallLoader />
                  ) : (
                    <>
                      <img src={downIcon} alt="download-icon" />
                      <button
                        type="button"
                        className={`download-template ${isDownloadDisabled ? "disabled-text" : ""}`}
                        disabled={isDownloadDisabled}
                      >
                        Download
                      </button>
                    </>
                  )}
                </div>
              </div>
            </div>

            <div className="ckr-table-wrapper cdm-table">
              {loader ? (
                <div className="loader-alignment">
                  <Loader />
                </div>
              ) : totalCount > 0 ? (
                <>
                  <table className="ckr-table draft-maker dm-fixed-cols">
                    <thead>
                      <tr>
                        <th className="dm-col-sr">SR. No</th>
                        <th className="dm-col-req-type">Request Type</th>
                        <th className="dm-col-applicant">Applicant</th>
                        <th className="dm-col-date">Request Date</th>
                        <th className="dm-col-count">Records</th>
                        <th className="dm-col-reason-type">Reason Type</th>
                        <th className="dm-col-reason">Remarks</th>
                        <th className="dm-col-status">Status</th>
                        <th className="dm-col-action">Action</th>
                      </tr>
                    </thead>
                    <tbody>
                      {paginatedData.map((batch, index) => {
                        const requests = batch?.requests || [];
                        const first = requests[0] || {};

                        return (
                          <tr key={batch?.batchId || index}>
                            <td>{(page - 1) * limit + index + 1}</td>
                            <td>{batch?.batchId || "-"}</td>
                            <td>{batch?.makerName || batch?.makerId || "-"}</td>
                            <td>{batch?.submittedAt ? formatDate(batch.submittedAt) : "-"}</td>
                            <td>{requests.length || batch?.totalValidCount || 0}</td>
                            {/* <td>{getReasonTypeLabel(batch?.reason || batch?.reasonType ? batch : first)}</td> */}
                            {/* A bulk batch shows what the maker entered for the whole batch,
                                never the first row's own reason. */}
                            <td>
                              {getReasonTypeLabel(
                                batch?.reason || batch?.reasonType || isBulkBatch(batch) ? batch : first
                              )}
                            </td>
                            {/* <td>{renderRemarks(batch?.remarks || (isBulkBatch(batch) ? "" : first?.remarks))}</td> */}
                            <td className="dm-col-reason">{renderRemarks(batch?.remarks || (isBulkBatch(batch) ? "" : first?.remarks))}</td>
                            <td className="dm-col-status">
                              <span className="status-pending">Pending</span>
                            </td>
                            <td>
                              <div className="view-btn">
                                <button
                                  type="button"
                                  className="submit-btns-view"
                                  onClick={() => handleViewDetails(batch)}
                                >
                                  View Details
                                </button>
                              </div>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>

                  <CheckerPagination
                    page={page}
                    limit={limit}
                    totalCount={totalCount}
                    onPageChange={setPage}
                    onLimitChange={(value) => {
                      setLimit(value);
                      setPage(1);
                    }}
                  />
                </>
              ) : (
                <div className="step-content report-error-message">{noDataFound()}</div>
              )}
            </div>
          </div>
        </div>
      </div>

      <Modal
        show={filterModal}
        onHide={handleCloseFilter}
        size="lg"
        centered
        className="inactive-modal setting-model cdm-date-modal"
        backdrop="static"
        keyboard={true}
      >
        <Modal.Header className="mb-4 modal-header">
          <Modal.Title>
            <div className="figure-inlac header-pnl">
              <span className="header-title">Filters</span>
            </div>
          </Modal.Title>
          <button type="button" className="btn-close" aria-label="Close" onClick={handleCloseFilter}>
            <span className="icon-close"></span>
          </button>
        </Modal.Header>
        <Modal.Body>
          <div className="table-responsive scroll-table model-top-active-table">
            <span className="body-title-main">Select Type</span>
            <div className="type-wrapper" role="radiogroup" aria-label="Select Type">
              {[
                ["all", "All type"],
                ["single", "Single"],
                ["bulk", "Bulk"],
              ].map(([value, label]) => (
                <button
                  key={value}
                  type="button"
                  role="radio"
                  aria-checked={typeFilter === value}
                  className={`type-button ${typeFilter === value ? "active" : ""}`}
                  onClick={() => setTypeFilter(value)}
                >
                  {label}
                </button>
              ))}
            </div>

            <span className="body-title-main">Custom Date Range</span>
            <div className="date-wrapper">
              <div className="form-group">
                <label className="form-label">
                  From Date<span className="manadate-mark">*</span>
                </label>
                <CustomTradeDate
                  idPrefix="pendingFromDate"
                  value={fromDate}
                  onChange={setFromDate}
                  maxDate={toDate || currentDate}
                />
              </div>
              <div className="form-group">
                <label className="form-label">
                  To Date<span className="manadate-mark">*</span>
                </label>
                <CustomTradeDate
                  idPrefix="pendingToDate"
                  value={toDate}
                  onChange={setToDate}
                  minDate={fromDate}
                  maxDate={currentDate}
                />
              </div>
            </div>

            <div className="info-banner">
              <img src={infoIcon} alt="info icon" />
              <span className="banner-text">
                Please select a date range where difference between From date and To date is less than 1 month
              </span>
            </div>

            <div className="footer-actions modal-footer">
              <button type="button" className="back-btn" onClick={handleResetFilter}>
                Reset
              </button>
              <button type="button" className="submit-btns" onClick={handleApplyFilter}>
                Apply
              </button>
            </div>
          </div>
        </Modal.Body>
      </Modal>
    </>
  );
}

export default PendingApprovals;
